import {
  EXISTING_DISCOUNT_RULE,
  EXISTING_DISCOUNT_RULES,
  RENEWAL_DISCOUNT_CALCULATION,
  RENEWAL_DISCOUNT_CALCULATIONS,
  existingDiscountRuleSchema,
  renewalDiscountCalculationSchema,
  type OperationDetail,
  type RenewalDiscountOperationInput,
} from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import type { JSX } from 'react';
import { useReportDirty } from '@/components/forms/unsaved-changes-guard.js';
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { PageSection } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { SubmitErrorBanner } from './operation-ui.js';
import { OptionCards } from './option-cards.js';
import { TagChipInput } from './tag-chip-input.js';

/**
 * Formulaire d'une remise sur le prochain renouvellement (`07` §1, §3) :
 * période des renouvellements visés (jours de Paris), ciblage, calcul,
 * paliers par nombre de produits, règle pour un code existant.
 *
 * Une fois l'opération activée, seul le nom reste
 * modifiable : le reste a déjà servi à poser des remises.
 */

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const renewalFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Saisissez un nom.'),
    periodStart: z.string().regex(DAY, 'Choisissez la date du premier renouvellement.'),
    periodEnd: z.string().regex(DAY, 'Choisissez la date du dernier renouvellement.'),
    includeNewSubscribers: z.boolean(),
    excludeLegacy: z.boolean(),
    /** Affichage seulement : décoché, la liste de SKU est vidée à l'enregistrement. */
    filterProducts: z.boolean(),
    productSkus: z.array(z.string().min(1)),
    calculation: renewalDiscountCalculationSchema,
    tiers: z.array(z.object({ minProducts: z.number(), percent: z.number() })),
    existingDiscountRule: existingDiscountRuleSchema,
  })
  .superRefine((values, ctx) => {
    if (DAY.test(values.periodStart) && DAY.test(values.periodEnd) && values.periodEnd < values.periodStart) {
      ctx.addIssue({ code: 'custom', path: ['periodEnd'], message: 'Choisissez une date postérieure au premier renouvellement.' });
    }
    if (values.filterProducts && values.productSkus.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['productSkus'], message: 'Ajoutez au moins un SKU, ou décochez le filtre.' });
    }
    if (values.tiers.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['tiers'], message: 'Ajoutez au moins un palier.' });
    }
    const seen = new Set<number>();
    values.tiers.forEach((tier, index) => {
      if (!Number.isInteger(tier.minProducts) || tier.minProducts < 1 || tier.minProducts > 100) {
        ctx.addIssue({ code: 'custom', path: ['tiers', index, 'minProducts'], message: 'Saisissez un nombre entier entre 1 et 100.' });
      } else if (seen.has(tier.minProducts)) {
        ctx.addIssue({ code: 'custom', path: ['tiers', index, 'minProducts'], message: 'Palier déjà défini.' });
      }
      seen.add(tier.minProducts);
      if (!Number.isFinite(tier.percent) || tier.percent < 1 || tier.percent > 100) {
        ctx.addIssue({ code: 'custom', path: ['tiers', index, 'percent'], message: 'Saisissez un taux entre 1 et 100 %.' });
      }
    });
  });

export type RenewalDiscountFormValues = z.infer<typeof renewalFormSchema>;

export function renewalDefaultValues(detail: OperationDetail | undefined): RenewalDiscountFormValues {
  const config = detail?.renewalDiscount;
  if (!detail || !config) {
    return {
      name: '',
      periodStart: '',
      periodEnd: '',
      includeNewSubscribers: false,
      excludeLegacy: false,
      filterProducts: false,
      productSkus: [],
      calculation: 'additional',
      tiers: [{ minProducts: 1, percent: 10 }],
      existingDiscountRule: 'ignore',
    };
  }
  return {
    name: detail.name,
    periodStart: config.periodDays.start,
    periodEnd: config.periodDays.end,
    includeNewSubscribers: config.includeNewSubscribers,
    excludeLegacy: config.excludeLegacy,
    filterProducts: config.productSkus.length > 0,
    productSkus: [...config.productSkus],
    calculation: config.calculation,
    tiers: config.tiers.map((tier) => ({ ...tier })),
    existingDiscountRule: config.existingDiscountRule,
  };
}

export function toRenewalInput(values: RenewalDiscountFormValues): RenewalDiscountOperationInput {
  return {
    kind: 'renewal_discount',
    name: values.name.trim(),
    period: { start: values.periodStart, end: values.periodEnd },
    renewalDiscount: {
      includeNewSubscribers: values.includeNewSubscribers,
      excludeLegacy: values.excludeLegacy,
      productSkus: values.filterProducts ? values.productSkus : [],
      calculation: values.calculation,
      tiers: [...values.tiers].sort((a, b) => a.minProducts - b.minProducts),
      existingDiscountRule: values.existingDiscountRule,
    },
  };
}

const CALCULATION_OPTIONS = RENEWAL_DISCOUNT_CALCULATIONS.map((code) => ({
  value: code,
  label: RENEWAL_DISCOUNT_CALCULATION[code].label,
  description: RENEWAL_DISCOUNT_CALCULATION[code].description,
}));

const RULE_OPTIONS = EXISTING_DISCOUNT_RULES.map((code) => ({
  value: code,
  label: EXISTING_DISCOUNT_RULE[code].label,
  description: EXISTING_DISCOUNT_RULE[code].description,
}));

export interface RenewalDiscountFormProps {
  detail: OperationDetail | undefined;
  isSaving: boolean;
  onSubmit: (input: RenewalDiscountOperationInput) => void;
  /** Absent en création. */
  onArchive?: (() => void) | undefined;
  /** Échec du dernier enregistrement, affiché en tête du formulaire (la saisie est conservée). */
  submitError?: string | null | undefined;
  /** Remonte l'état modifié (garde de sortie, activation bloquée tant que rien n'est enregistré). */
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
}

export function RenewalDiscountForm({
  detail,
  isSaving,
  onSubmit,
  onArchive,
  submitError = null,
  onDirtyChange,
}: RenewalDiscountFormProps): JSX.Element {
  const form = useForm<RenewalDiscountFormValues>({
    resolver: zodResolver(renewalFormSchema) as Resolver<RenewalDiscountFormValues>,
    values: renewalDefaultValues(detail),
  });
  const tiers = useFieldArray({ control: form.control, name: 'tiers' });
  const watchedTiers = form.watch('tiers');
  const watchedFilterProducts = form.watch('filterProducts');
  const { errors, isDirty } = form.formState;
  const isNew = detail === undefined;
  const config = detail?.renewalDiscount ?? null;
  // `07` §4.3 : l'API refuse (409) toute autre modification d'une opération activée.
  const locked = config !== null && config.state !== 'draft';
  // Archivée : plus rien ne s'enregistre (la page affiche la bannière « Opération archivée »).
  const readOnly = detail?.archived === true;
  useReportDirty(isDirty, onDirtyChange);

  return (
    <form
      className="flex w-full max-w-[880px] flex-col gap-6"
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit((submitted) => {
          onSubmit(toRenewalInput(submitted));
        })(event);
      }}
    >
      <SubmitErrorBanner message={submitError} />
      <fieldset disabled={readOnly} className="flex min-w-0 flex-col gap-6">
      <PageSection>
        <div className="flex flex-col gap-5">
          {locked ? (
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              Opération activée : seul le nom reste modifiable.
            </p>
          ) : null}
          <Field label="Nom" htmlFor="renewal-name" error={errors.name?.message}>
            <Input id="renewal-name" aria-invalid={errors.name ? true : undefined} {...form.register('name')} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Premier renouvellement visé"
              htmlFor="renewal-start"
              hint="Date prévue du renouvellement, heure de Paris, incluse."
              error={errors.periodStart?.message}
            >
              <Input
                id="renewal-start"
                type="date"
                disabled={locked}
                aria-invalid={errors.periodStart ? true : undefined}
                {...form.register('periodStart')}
              />
            </Field>
            <Field label="Dernier renouvellement visé" htmlFor="renewal-end" error={errors.periodEnd?.message}>
              <Input
                id="renewal-end"
                type="date"
                disabled={locked}
                aria-invalid={errors.periodEnd ? true : undefined}
                {...form.register('periodEnd')}
              />
            </Field>
          </div>

          {config?.codePrefix ? (
            <Field label="Codes Recharge générés" hint="Un code par renouvellement remisé, fermé au checkout et au portail client.">
              <p className="font-mono text-sm" translate="no">{`${config.codePrefix}-…`}</p>
            </Field>
          ) : null}
        </div>
      </PageSection>

      <PageSection title="Clients visés" description="Un seul renouvellement remisé par client : le premier de la période.">
        <div className="flex flex-col gap-5">
          <Controller
            control={form.control}
            name="includeNewSubscribers"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="flex w-fit items-center gap-2 text-sm">
                  <Checkbox
                    checked={field.value}
                    disabled={locked}
                    onCheckedChange={(checked) => {
                      field.onChange(checked === true);
                    }}
                  />
                  Inclure les nouveaux abonnements au fil de l’opération
                </label>
                <p className="text-xs leading-5 text-muted-foreground">
                  Par défaut, seuls les abonnements déjà existants à la date d’activation de l’opération reçoivent la remise.
                </p>
              </div>
            )}
          />
          <Controller
            control={form.control}
            name="excludeLegacy"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="flex w-fit items-center gap-2 text-sm">
                  <Checkbox
                    checked={field.value}
                    disabled={locked}
                    onCheckedChange={(checked) => {
                      field.onChange(checked === true);
                    }}
                  />
                  Exclure les abonnements bénéficiant de la remise legacy
                </label>
                <p className="text-xs leading-5 text-muted-foreground">
                  Abonnements legacy pas encore migrés, ou migrés avec la remise legacy (même règle que la catégorie d’abonné Klaviyo).
                  Leurs produits ne comptent ni pour le palier ni pour le montant ; un renouvellement entièrement legacy ne reçoit rien.
                </p>
              </div>
            )}
          />
          <Controller
            control={form.control}
            name="filterProducts"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="flex w-fit items-center gap-2 text-sm">
                  <Checkbox
                    checked={field.value}
                    disabled={locked}
                    onCheckedChange={(checked) => {
                      field.onChange(checked === true);
                    }}
                  />
                  Filtrer sur les produits de l’abonnement
                </label>
                <p className="text-xs leading-5 text-muted-foreground">
                  Par défaut, tous les renouvellements sont visés. Cochez pour ne cibler que ceux qui contiennent certains produits.
                </p>
              </div>
            )}
          />
          {watchedFilterProducts ? (
            <Controller
              control={form.control}
              name="productSkus"
              render={({ field, fieldState }) => (
                <Field
                  label="Produits visés"
                  hint="Le renouvellement doit contenir un de ces SKU (composants de bundle compris)."
                  error={fieldState.error?.message}
                >
                  {locked ? (
                    <div className="flex flex-wrap gap-1.5">
                      {field.value.map((sku) => (
                        <Badge key={sku} tone="gray">
                          <span translate="no">{sku}</span>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <TagChipInput value={field.value} onChange={field.onChange} placeholder="GUM-…" aria-label="SKU visés" />
                  )}
                </Field>
              )}
            />
          ) : null}
        </div>
      </PageSection>

      <PageSection title="Remise">
        <div className="flex flex-col gap-5">
          <Controller
            control={form.control}
            name="calculation"
            render={({ field }) => (
              <OptionCards
                name="renewal-calculation"
                legend="Calcul"
                options={CALCULATION_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                disabled={locked}
              />
            )}
          />

          <fieldset className="flex min-w-0 flex-col gap-2" disabled={locked}>
            <legend className="mb-0.5 text-sm font-medium">Paliers</legend>
            <p className="text-xs leading-5 text-muted-foreground">
              Selon le nombre de produits du renouvellement, chaque composant d’un bundle compté à part. Le palier le plus
              haut atteint s’applique ; sous le premier palier, pas de remise.
            </p>
            <div className="flex flex-col gap-2">
              {tiers.fields.map((tier, index) => {
                const rowErrors = errors.tiers?.[index];
                const position = String(index + 1);
                const minError = `renewal-tier-${position}-min-error`;
                const rateError = `renewal-tier-${position}-rate-error`;
                const minProducts = watchedTiers[index]?.minProducts ?? 0;
                return (
                  <div key={tier.id} className="flex flex-wrap items-start gap-2 text-sm">
                    <span className="pt-2">À partir de</span>
                    <div className="flex flex-col">
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        className="w-20"
                        aria-label={`Nombre de produits du palier ${position}`}
                        aria-invalid={rowErrors?.minProducts ? true : undefined}
                        aria-describedby={rowErrors?.minProducts?.message ? minError : undefined}
                        {...form.register(`tiers.${index}.minProducts` as const, { valueAsNumber: true })}
                      />
                      {rowErrors?.minProducts?.message ? (
                        <p id={minError} className="mt-1 text-xs text-status-red-fg">
                          {rowErrors.minProducts.message}
                        </p>
                      ) : null}
                    </div>
                    <span className="pt-2">{Number.isFinite(minProducts) && minProducts >= 2 ? 'produits :' : 'produit :'}</span>
                    <div className="flex flex-col">
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        step="0.01"
                        className="w-24"
                        aria-label={`Taux du palier ${position}`}
                        aria-invalid={rowErrors?.percent ? true : undefined}
                        aria-describedby={rowErrors?.percent?.message ? rateError : undefined}
                        {...form.register(`tiers.${index}.percent` as const, { valueAsNumber: true })}
                      />
                      {rowErrors?.percent?.message ? (
                        <p id={rateError} className="mt-1 text-xs text-status-red-fg">
                          {rowErrors.percent.message}
                        </p>
                      ) : null}
                    </div>
                    <span className="pt-2">%</span>
                    {locked ? null : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Supprimer le palier ${position}`}
                        disabled={tiers.fields.length <= 1}
                        onClick={() => {
                          tiers.remove(index);
                        }}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            {errors.tiers?.root?.message ?? errors.tiers?.message ? (
              <p className="text-xs text-status-red-fg">{errors.tiers.root?.message ?? errors.tiers.message}</p>
            ) : null}
            {locked ? null : (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const highest = Math.max(0, ...form.getValues('tiers').map((value) => value.minProducts || 0));
                    tiers.append({ minProducts: highest + 1, percent: 10 });
                  }}
                >
                  <Plus aria-hidden="true" />
                  Ajouter un palier
                </Button>
              </div>
            )}
          </fieldset>

          <Controller
            control={form.control}
            name="existingDiscountRule"
            render={({ field }) => (
              <OptionCards
                name="renewal-existing-rule"
                legend="Si le renouvellement porte déjà un code de réduction"
                options={RULE_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                disabled={locked}
              />
            )}
          />

        </div>
      </PageSection>

      </fieldset>

      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-2">
          {onArchive ? (
            <Button type="button" variant="outline" onClick={onArchive}>
              Archiver…
            </Button>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <SaveButton isLoading={isSaving} isDirty={isNew || isDirty} />
          </div>
        </div>
      )}
    </form>
  );
}
